/**
 * Every payment a customer has made on a job, in one list (ADR 0288).
 *
 * Jonathon, 2026-10-14: "there needs to be a payments tab on each job where we
 * can see the payments the customer has made." Asked what it lists: every
 * payment, from wherever it came.
 *
 * Money reaches a job three ways, and one payment can be seen by more than one
 * of them, so the work here is saying each payment once:
 *
 *  - **Entered by hand** — a check, cash, a card taken outside Roof Board, a
 *    bank transfer, a financing payout (`customer_payments`). Always listed.
 *  - **A credit in the Mercury account** confirmed against the job. Listed
 *    unless a hand-entered payment already points at it: the check somebody
 *    recorded and the check deposit are the same money. So are a transfer
 *    somebody typed in and its credit, once one is tied to the other
 *    (`creditThisPaymentIs`, `recordedPaymentThisCreditIs`). Until then the
 *    credit is listed in the bank and the transfer on its way, which is what
 *    the balance counts too. Nothing here pairs money by itself.
 *  - **A payment link the customer paid** whose money has not landed yet — a
 *    card at Stripe for two or three days. Listed as on its way, and dropped
 *    once its credit is confirmed, because then the credit is the payment.
 *    A person can say it is already in the bank (ADR 0312): the record they
 *    make is then the line, and neither the link nor its credit, when that
 *    comes, is listed beside it.
 *
 * Nothing here decides what the customer owes; that is `jobBalance`. This is
 * the list a person reads to see what came in and when.
 */

import { PAYMENT_SLOT_LABEL } from "./constants.js";
import {
  isCheckDeposit,
  mayMarkInTheBank,
  paymentMethodText,
  type CustomerPaymentMethod,
} from "./customerPayment.js";
import { cents, formatMoney, type Cents } from "./money.js";
import { linksWhoseCreditLanded } from "./linksWhoseCreditLanded.js";

/** A payment somebody entered by hand. */
export interface RecordedPayment {
  id: string;
  method: CustomerPaymentMethod;
  amountCents: number;
  /** `YYYY-MM-DD`. */
  receivedOn: string;
  state: "submitted" | "paid";
  slots: readonly number[];
  towardPlan?: boolean;
  checkNumber?: string | null;
  receiptNumber?: string | null;
  mercuryDepositId?: string | null;
  /** The payment link this is the money for, said in the bank by a person (ADR 0312). */
  mercuryRequestId?: string | null;
}

/** A credit in the Mercury account, confirmed against this job. */
export interface ConfirmedDeposit {
  id: string;
  postedAt: string | null;
  amountCents: number;
  /** What the customer was charged, where a batched payout was split. */
  chargeCents?: number | null;
  slots: readonly number[] | null;
  description?: string | null;
}

/** A Mercury payment request — a link the customer was sent. */
export interface LinkPayment {
  id: string;
  slot: number | null;
  state: string;
  amountCents: number | null;
  paymentStartedAt: string | null;
  paidAt?: string | null;
  /** card, ach or financing. */
  customerMethod: string | null;
  mercuryDepositId: string | null;
}

export interface PaymentHistoryRow {
  key: string;
  /** `YYYY-MM-DD`, for sorting and for printing. */
  date: string;
  amount: Cents;
  amountFormatted: string;
  /** "Check no. 2047", "Card (payment link)". */
  how: string;
  /** "First Payment", "Payment plan", "Other". */
  forWhat: string;
  inTheBank: boolean;
  /**
   * On its way, and a person may say it has landed: a payment entered by
   * hand and not yet in, or a link the customer paid whose credit the bank
   * has not shown (ADR 0288, ADR 0312).
   */
  awaitsAPerson: boolean;
  /** The hand-entered payment behind this row, which is the only kind a person can remove. */
  recordedId: string | null;
  /** The paid link behind this row, which a person may say is in the bank. */
  linkId: string | null;
  receiptNumber: string | null;
}

const LINK_METHOD: Record<string, string> = {
  card: "Card (payment link)",
  ach: "Bank (payment link)",
  financing: "Financing (payment link)",
};

function forWhat(slots: readonly number[] | null, towardPlan = false): string {
  if (towardPlan) return "Payment plan";
  const named = (slots ?? []).map(
    (slot) => PAYMENT_SLOT_LABEL[slot] ?? `Payment ${slot}`,
  );
  return named.length ? named.join(" and ") : "Other";
}

const day = (at: string | null | undefined): string => (at ?? "").slice(0, 10);

function row(
  parts: Omit<PaymentHistoryRow, "amount" | "amountFormatted"> & {
    amount: number;
  },
): PaymentHistoryRow {
  const amount = cents(Math.max(0, Math.round(parts.amount)));
  return { ...parts, amount, amountFormatted: formatMoney(amount) };
}

type HistoryInput = {
  recorded: readonly RecordedPayment[];
  deposits: readonly ConfirmedDeposit[];
  links: readonly LinkPayment[];
};

const linkHow = (link: LinkPayment | undefined): string | null =>
  link?.customerMethod
    ? (LINK_METHOD[link.customerMethod] ?? "Payment link")
    : null;

/**
 * The links a person has recorded as in the bank, by link id. `paid` only
 * for the ones that record still says are in.
 */
const recordedLinks = (input: HistoryInput, paid = false): Set<string> =>
  new Set(
    input.recorded
      .filter((p) => !paid || p.state === "paid")
      .map((p) => p.mercuryRequestId)
      .filter((id): id is string => Boolean(id)),
  );

/**
 * Hand-entered payments: always listed, and in the bank once paid — the same
 * fact `jobBalance` reads, so the tab and the balance never disagree.
 *
 * Except a link's money said to be in the bank and since put back to owed,
 * once the link's own credit has landed: the credit is then the line, in the
 * bank, as the balance counts it (ADR 0312).
 */
function recordedRows(input: HistoryInput): PaymentHistoryRow[] {
  const landed = linksWhoseCreditLanded(
    input.links,
    new Set(input.deposits.map((d) => d.id)),
  );
  return input.recorded
    .filter(
      (p) =>
        p.state === "paid" ||
        !p.mercuryRequestId ||
        !landed.has(p.mercuryRequestId),
    )
    .map((p) =>
      row({
        key: `recorded:${p.id}`,
        date: day(p.receivedOn),
        amount: p.amountCents,
        how:
          paymentMethodText(p.method, p.checkNumber) +
          (p.mercuryRequestId ? " (payment link)" : ""),
        forWhat: forWhat(p.slots, p.towardPlan),
        inTheBank: p.state === "paid",
        awaitsAPerson: mayMarkInTheBank(p),
        recordedId: p.id,
        linkId: null,
        // Mercury receipted the link; the number this row was given is unused.
        receiptNumber: p.mercuryRequestId ? null : (p.receiptNumber ?? null),
      }),
    );
}

/** Credits in the account, less those a hand-entered payment already is. */
function depositRows(input: HistoryInput): PaymentHistoryRow[] {
  const claimed = new Set(
    input.recorded
      .map((p) => p.mercuryDepositId)
      .filter((id): id is string => Boolean(id)),
  );
  const linkByDeposit = new Map(
    input.links
      .filter((link) => link.mercuryDepositId)
      .map((link) => [link.mercuryDepositId as string, link]),
  );
  // A credit for a link somebody already said was in the bank is that
  // record's money, the way a check deposit is the recorded check's — while
  // the record says so. One put back to owed leaves the credit as the line.
  const saidInTheBank = recordedLinks(input, true);
  return input.deposits
    .filter((d) => !claimed.has(d.id))
    .filter((d) => {
      const link = linkByDeposit.get(d.id);
      return !(link && saidInTheBank.has(link.id));
    })
    .map((d) => {
      const link = linkByDeposit.get(d.id);
      // What the customer paid, not what reached the bank after the card
      // processor's cut: the link's own amount, or the charge on a split
      // payout.
      const billed = Number(link?.amountCents ?? 0);
      return row({
        key: `deposit:${d.id}`,
        date: day(d.postedAt),
        amount: billed > 0 ? billed : (d.chargeCents ?? d.amountCents),
        how:
          linkHow(link) ??
          (isCheckDeposit({ description: d.description ?? null })
            ? "Check (deposited)"
            : "Bank deposit"),
        forWhat: forWhat(d.slots ?? (link?.slot ? [link.slot] : null)),
        inTheBank: true,
        awaitsAPerson: false,
        recordedId: null,
        linkId: null,
        receiptNumber: null,
      });
    });
}

/** When the customer started paying a link, or null if they have not. */
function linkStarted(link: LinkPayment): string | null {
  if (link.state === "failed" || link.state === "cancelled") return null;
  return (
    link.paymentStartedAt ??
    (link.state === "paid" ? (link.paidAt ?? null) : null)
  );
}

/**
 * Links paid whose money has not landed: on their way, and a person may say
 * they have landed (ADR 0312).
 *
 * A link whose slot a confirmed credit or a paid payment already covers is
 * not on its way, tied to that credit or not: the balance skips it the same
 * way (`alreadyBeingCollected`), so the tab and the Balance card agree. Nor
 * is a link a person has already recorded as in the bank: that record is
 * the line.
 */
function linkRows(input: HistoryInput): PaymentHistoryRow[] {
  const confirmed = new Set(input.deposits.map((d) => d.id));
  const saidInTheBank = recordedLinks(input);
  const covered = new Set<number>([
    ...input.deposits.flatMap((d) => d.slots ?? []),
    ...input.recorded.filter((p) => p.state === "paid").flatMap((p) => p.slots),
  ]);
  return input.links
    .filter(
      (link) =>
        linkStarted(link) !== null &&
        !saidInTheBank.has(link.id) &&
        !(link.mercuryDepositId && confirmed.has(link.mercuryDepositId)) &&
        !(link.slot != null && covered.has(Number(link.slot))),
    )
    .map((link) =>
      row({
        key: `link:${link.id}`,
        date: day(linkStarted(link)),
        amount: Number(link.amountCents ?? 0),
        how: linkHow(link) ?? "Payment link",
        forWhat: forWhat(link.slot ? [link.slot] : null),
        inTheBank: false,
        awaitsAPerson: true,
        recordedId: null,
        linkId: link.id,
        receiptNumber: null,
      }),
    );
}

/** Every payment on the job, newest first, each once. */
export function paymentHistory(input: HistoryInput): PaymentHistoryRow[] {
  return [
    ...recordedRows(input),
    ...depositRows(input),
    ...linkRows(input),
  ].sort((a, b) => b.date.localeCompare(a.date) || a.key.localeCompare(b.key));
}

/** What the list adds up to, in the bank and on its way. */
export function paymentHistoryTotals(rows: readonly PaymentHistoryRow[]): {
  inTheBank: Cents;
  onItsWay: Cents;
} {
  let bank = 0;
  let way = 0;
  for (const r of rows) {
    if (r.inTheBank) bank += r.amount;
    else way += r.amount;
  }
  return { inTheBank: cents(bank), onItsWay: cents(way) };
}
